export const POSITION_EVENT_TYPE_API = 'com.trimble.trans.track.position.event';

export const SHIPMENT_EVENT_TYPE_API = 'com.trimble.trans.track.shipment.event';

export const REFERENCE_NUMBERS_MAP_FIELD = 'ReferenceNumbers';

/** Bracket-predicate fields available on all event types. */
export const IDENTIFIER_FIELD_OPTIONS = Object.freeze([
    'CarrierIdentifiers',
    'ReferenceNumbers',
    'ReferenceNumbersList',
    'ShipperIdentifiers'
]);

export const SHIPPER_IDENTIFIERS_FIELD = 'ShipperIdentifiers';

export const CARRIER_IDENTIFIERS_FIELD = 'CarrierIdentifiers';

export const CUSTOMER_REFERENCE_FIELD = 'CustomerReference';

function escapeJsonataSingleQuotedString(s) {
    return String(s).replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

/**
 * @param {string} [eventTypeKey]
 * @returns {boolean}
 */
function useNumericJsonataScalarForRhs(eventTypeKey) {
    return String(eventTypeKey ?? '').trim() === POSITION_EVENT_TYPE_API;
}

/**
 * @param {unknown} raw
 * @param {string} [eventTypeKey]
 * @param {boolean} [forceStringScalar]
 * @returns {string|null}
 */
function formatJsonataRhs(raw, eventTypeKey, forceStringScalar = false) {
    const t = String(raw ?? '').trim();
    if (t === '') return null;
    const numericScalar = !forceStringScalar && useNumericJsonataScalarForRhs(eventTypeKey);
    try {
        const p = JSON.parse(t);
        if (Array.isArray(p)) return JSON.stringify(p);
        if (typeof p === 'object' && p !== null) return JSON.stringify(p);
        if (p === null) return 'null';
        if (typeof p === 'boolean') return p ? 'true' : 'false';
        if (typeof p === 'number') {
            if (numericScalar) return String(p);
            return `'${String(p)}'`;
        }
        if (typeof p === 'string') return `'${escapeJsonataSingleQuotedString(p)}'`;
    } catch {
        return `'${escapeJsonataSingleQuotedString(t)}'`;
    }
}

/**
 * @param {string} fieldId
 * @returns {string}
 */
export function identifierFieldMenuLabel(fieldId) {
    if (fieldId === SHIPPER_IDENTIFIERS_FIELD) {
        return 'ShipperIdentifiers (Org identifier type 3)';
    }
    if (fieldId === CARRIER_IDENTIFIERS_FIELD) {
        return 'CarrierIdentifiers (OrganizationIdentifierType)';
    }
    if (fieldId === CUSTOMER_REFERENCE_FIELD) {
        return 'CustomerReference (Orders)';
    }
    return fieldId;
}

/**
 * @param {string} field
 * @param {string} [eventTypeKey]
 * @returns {boolean}
 */
export function isAllowedSelectorField(field, eventTypeKey) {
    if (IDENTIFIER_FIELD_OPTIONS.includes(field)) {
        return true;
    }
    if (field === CUSTOMER_REFERENCE_FIELD) {
        return String(eventTypeKey ?? '').trim() === SHIPMENT_EVENT_TYPE_API;
    }
    return false;
}

/**
 * Selector Field dropdown options for the current event type.
 * @param {string} [eventTypeKey]
 * @returns {string[]}
 */
export function selectorFieldOptionsForEvent(eventTypeKey) {
    const options = [...IDENTIFIER_FIELD_OPTIONS];
    if (String(eventTypeKey ?? '').trim() === SHIPMENT_EVENT_TYPE_API) {
        options.push(CUSTOMER_REFERENCE_FIELD);
    }
    return options;
}

/**
 * Builds a JSONata selector predicate for QuickAdd.
 * @param {string} fieldName
 * @param {string} listKey
 * @param {string} op
 * @param {unknown} valueRaw
 * @param {string} [eventTypeKey]
 * @returns {{ expr?: string, error?: string }}
 */
export function buildIdentifierBracketSelector(fieldName, listKey, op, valueRaw, eventTypeKey) {
    const field = String(fieldName ?? '').trim();
    if (!field || !isAllowedSelectorField(field, eventTypeKey)) {
        return { error: 'Choose a field.' };
    }
    const o = String(op ?? '').trim() || '=';
    const forceStringScalarForPositionRefs =
        String(eventTypeKey ?? '').trim() === POSITION_EVENT_TYPE_API &&
        (field === REFERENCE_NUMBERS_MAP_FIELD || field === 'ReferenceNumbersList');
    const forceStringScalarForOrgIdentifiers =
        field === SHIPPER_IDENTIFIERS_FIELD ||
        field === CARRIER_IDENTIFIERS_FIELD ||
        field === CUSTOMER_REFERENCE_FIELD;
    const rhs = formatJsonataRhs(
        valueRaw,
        eventTypeKey,
        forceStringScalarForOrgIdentifiers || forceStringScalarForPositionRefs
    );
    if (rhs === null) {
        return {
            error: 'Enter a value (plain text, number, true/false/null, or JSON such as an array for `in`).'
        };
    }
    if (field === CUSTOMER_REFERENCE_FIELD) {
        const expr = `$.Orders[*].CustomerReference${o}${rhs}`;
        return { expr };
    }
    if (field === SHIPPER_IDENTIFIERS_FIELD) {
        const expr = `${field}[OrganizationIdentifierType=3].Value${o}${rhs}`;
        return { expr };
    }
    if (field === CARRIER_IDENTIFIERS_FIELD) {
        const typeRaw = String(listKey ?? '').trim();
        if (!typeRaw) {
            return { error: 'Enter an organization identifier type (e.g. 1, 5, 6).' };
        }
        const typeNum = Number(typeRaw);
        if (!Number.isInteger(typeNum) || typeNum < 0) {
            return { error: 'Organization identifier type must be a non-negative integer.' };
        }
        const expr = `${field}[OrganizationIdentifierType=${typeNum}].Value${o}${rhs}`;
        return { expr };
    }
    const k = String(listKey ?? '').trim();
    if (!k) {
        return { error: 'Enter a key.' };
    }
    const keyLiteral = k.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
    const expr =
        field === REFERENCE_NUMBERS_MAP_FIELD
            ? `${field}['${keyLiteral}']${o}${rhs}`
            : `${field}[Key='${keyLiteral}'].Value${o}${rhs}`;
    return { expr };
}
